import axios from 'axios';
import type {
  RacingEvent,
  EventClass,
  PaymentPackage,
  Registration,
  RegistrationClassEntry,
  PaginatedResult,
  AuthResponse,
  AuthProvider,
  Track,
  SanctioningBody,
  Role,
  UserWithRoles,
  AssignedUser,
  RaceLaneResult,
  RaceDataSearchParams,
  RaceDataCorrection,
  CorrectionHistoryResponse,
  AppUser,
  AppUserVehicle,
} from '../types';

let logoutCallback: (() => void) | null = null;

export const setLogoutCallback = (cb: () => void) => {
  logoutCallback = cb;
};

const api = axios.create({
  // Same-origin by default: in production the Express server serves this bundle
  // and the API, so a relative '/api' needs no CORS and keeps the JWT on one
  // origin. Set VITE_API_BASE_URL at BUILD time only if the API is deployed to a
  // different origin (e.g. the client on Static Web Apps, the API on App
  // Service) — the value is inlined into the bundle, not read at runtime, so a
  // change needs a rebuild.
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
});

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 401 = not authenticated (missing/expired/invalid token) -> log out.
    // 403 = authenticated but not permitted -> let the caller surface an error.
    if (error.response?.status === 401) {
      // Don't redirect if we're already on the login page (e.g. bad credentials)
      if (!window.location.pathname.startsWith('/login')) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        if (logoutCallback) {
          logoutCallback();
        } else {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (username: string, password: string) =>
  api.post<AuthResponse>('/auth/login', { username, password }).then((r) => r.data);

export const entraLogin = (idToken: string) =>
  api.post<AuthResponse>('/auth/entra', { idToken }).then((r) => r.data);

// Tracks
export const getTracks = () =>
  api.get<Track[]>('/tracks').then((r) => r.data);

export const getTrack = (id: number) =>
  api.get<Track>(`/tracks/${id}`).then((r) => r.data);

export const createTrack = (data: Partial<Track>) =>
  api.post<Track>('/tracks', data).then((r) => r.data);

export const updateTrack = (id: number, data: Partial<Track>) =>
  api.put<Track>(`/tracks/${id}`, data).then((r) => r.data);

export const deleteTrack = (id: number) =>
  api.delete(`/tracks/${id}`).then((r) => r.data);

export const getTrackUsers = (trackId: number) =>
  api.get<AssignedUser[]>(`/tracks/${trackId}/users`).then((r) => r.data);

// Sanctioning Bodies
export const getSanctioningBodies = () =>
  api.get<SanctioningBody[]>('/sanctioning-bodies').then((r) => r.data);

export const getSanctioningBody = (id: number) =>
  api.get<SanctioningBody>(`/sanctioning-bodies/${id}`).then((r) => r.data);

export const createSanctioningBody = (data: Partial<SanctioningBody>) =>
  api.post<SanctioningBody>('/sanctioning-bodies', data).then((r) => r.data);

export const updateSanctioningBody = (id: number, data: Partial<SanctioningBody>) =>
  api.put<SanctioningBody>(`/sanctioning-bodies/${id}`, data).then((r) => r.data);

export const deleteSanctioningBody = (id: number) =>
  api.delete(`/sanctioning-bodies/${id}`).then((r) => r.data);

export const getSanctioningBodyUsers = (sbId: number) =>
  api.get<AssignedUser[]>(`/sanctioning-bodies/${sbId}/users`).then((r) => r.data);

// Events
export const getEvents = () =>
  api.get<RacingEvent[]>('/events').then((r) => r.data);

export const getEvent = (id: number) =>
  api.get<RacingEvent>(`/events/${id}`).then((r) => r.data);

export const createEvent = (data: Partial<RacingEvent>) =>
  api.post<RacingEvent>('/events', data).then((r) => r.data);

export const updateEvent = (id: number, data: Partial<RacingEvent>) =>
  api.put<RacingEvent>(`/events/${id}`, data).then((r) => r.data);

export const deleteEvent = (id: number) =>
  api.delete(`/events/${id}`).then((r) => r.data);

// Event Classes
export const getEventClasses = (eventId: number) =>
  api.get<EventClass[]>(`/events/${eventId}/classes`).then((r) => r.data);

export const createEventClass = (eventId: number, data: Partial<EventClass>) =>
  api.post<EventClass>(`/events/${eventId}/classes`, data).then((r) => r.data);

export const updateEventClass = (eventId: number, classId: number, data: Partial<EventClass>) =>
  api.put<EventClass>(`/events/${eventId}/classes/${classId}`, data).then((r) => r.data);

export const deleteEventClass = (eventId: number, classId: number) =>
  api.delete(`/events/${eventId}/classes/${classId}`).then((r) => r.data);

// Payment Packages
export const getPaymentPackages = (eventId: number) =>
  api.get<PaymentPackage[]>(`/events/${eventId}/packages`).then((r) => r.data);

export const createPaymentPackage = (eventId: number, data: Partial<PaymentPackage>) =>
  api.post<PaymentPackage>(`/events/${eventId}/packages`, data).then((r) => r.data);

export const updatePaymentPackage = (eventId: number, packageId: number, data: Partial<PaymentPackage>) =>
  api.put<PaymentPackage>(`/events/${eventId}/packages/${packageId}`, data).then((r) => r.data);

export const deletePaymentPackage = (eventId: number, packageId: number) =>
  api.delete(`/events/${eventId}/packages/${packageId}`).then((r) => r.data);

// Registrations
export const getRegistrations = (
  eventId: number,
  params?: { page?: number; pageSize?: number; sortField?: string; sortOrder?: string; [key: string]: any }
) =>
  api.get<PaginatedResult<Registration>>(`/events/${eventId}/registrations`, { params }).then((r) => r.data);

export const getRegistration = (eventId: number, regId: number) =>
  api.get<Registration>(`/events/${eventId}/registrations/${regId}`).then((r) => r.data);

export const createRegistration = (eventId: number, data: Partial<Registration>) =>
  api.post<Registration>(`/events/${eventId}/registrations`, data).then((r) => r.data);

export const updateRegistration = (eventId: number, regId: number, data: Partial<Registration>) =>
  api.put<Registration>(`/events/${eventId}/registrations/${regId}`, data).then((r) => r.data);

export const deleteRegistration = (eventId: number, regId: number) =>
  api.delete(`/events/${eventId}/registrations/${regId}`).then((r) => r.data);

// Registration Class Entries
export const getClassEntries = (eventId: number, regId: number) =>
  api.get<RegistrationClassEntry[]>(`/events/${eventId}/registrations/${regId}/entries`).then((r) => r.data);

export const createClassEntry = (eventId: number, regId: number, data: Partial<RegistrationClassEntry>) =>
  api.post<RegistrationClassEntry>(`/events/${eventId}/registrations/${regId}/entries`, data).then((r) => r.data);

export const updateClassEntry = (eventId: number, regId: number, entryId: number, data: Partial<RegistrationClassEntry>) =>
  api.put<RegistrationClassEntry>(`/events/${eventId}/registrations/${regId}/entries/${entryId}`, data).then((r) => r.data);

export const deleteClassEntry = (eventId: number, regId: number, entryId: number) =>
  api.delete(`/events/${eventId}/registrations/${regId}/entries/${entryId}`).then((r) => r.data);

export const replaceClassEntries = (eventId: number, regId: number, entries: Partial<RegistrationClassEntry>[]) =>
  api.put<RegistrationClassEntry[]>(`/events/${eventId}/registrations/${regId}/entries`, { entries }).then((r) => r.data);

// Export
export const exportCf9 = (eventId: number) =>
  api.get(`/export/events/${eventId}/cf9`, { responseType: 'blob' }).then((r) => r.data);

export const exportIdx14 = (eventId: number) =>
  api.get(`/export/events/${eventId}/idx14`, { responseType: 'blob' }).then((r) => r.data);

export const exportDataInputForm = (eventId: number) =>
  api.get(`/export/events/${eventId}/data-input-form`, { responseType: 'blob' }).then((r) => r.data);

// Race Data
export const searchRaceData = (params: RaceDataSearchParams) =>
  api.get<PaginatedResult<RaceLaneResult>>('/race-data', { params }).then((r) => r.data);

export const getRaceRecord = (id: number) =>
  api.get<RaceLaneResult>(`/race-data/${id}`).then((r) => r.data);

/** @deprecated Use createCorrection() instead */
export const updateRaceRecord = (id: number, data: Partial<RaceLaneResult>) =>
  api.put<RaceLaneResult>(`/race-data/${id}`, data).then((r) => r.data);

// Race Data Corrections
export const createCorrection = (id: number, fields: Record<string, any>, reason?: string) =>
  api.post<RaceDataCorrection>(`/race-data/${id}/corrections`, { fields, reason }).then((r) => r.data);

export const getCorrectionHistory = (id: number) =>
  api.get<CorrectionHistoryResponse>(`/race-data/${id}/corrections`).then((r) => r.data);

export const voidCorrection = (correctionId: number) =>
  api.post<RaceDataCorrection>(`/race-data/corrections/${correctionId}/void`).then((r) => r.data);

export const rollbackCorrection = (id: number, versionNo: number, reason?: string) =>
  api.post<RaceDataCorrection>(`/race-data/${id}/corrections/rollback/${versionNo}`, { reason }).then((r) => r.data);

// Admin - Users
export const getUsers = () =>
  api.get<UserWithRoles[]>('/admin/users').then((r) => r.data);

export const getUser = (id: number) =>
  api.get<UserWithRoles>(`/admin/users/${id}`).then((r) => r.data);

export const createUser = (data: {
  username: string;
  password?: string;
  email?: string;
  display_name?: string;
  auth_provider?: AuthProvider;
  entra_upn?: string;
}) => api.post<UserWithRoles>('/admin/users', data).then((r) => r.data);

export const updateUser = (id: number, data: Partial<UserWithRoles>) =>
  api.put<UserWithRoles>(`/admin/users/${id}`, data).then((r) => r.data);

export const resetUserPassword = (id: number, password: string) =>
  api.post(`/admin/users/${id}/reset-password`, { password }).then((r) => r.data);

export const assignRole = (userId: number, roleId: number) =>
  api.post<UserWithRoles>(`/admin/users/${userId}/roles`, { role_id: roleId }).then((r) => r.data);

export const removeRole = (userId: number, roleId: number) =>
  api.delete<UserWithRoles>(`/admin/users/${userId}/roles/${roleId}`).then((r) => r.data);

export const assignTrackToUser = (userId: number, trackId: number) =>
  api.post<UserWithRoles>(`/admin/users/${userId}/tracks`, { track_id: trackId }).then((r) => r.data);

export const removeTrackFromUser = (userId: number, trackId: number) =>
  api.delete<UserWithRoles>(`/admin/users/${userId}/tracks/${trackId}`).then((r) => r.data);

export const assignSanctioningBodyToUser = (userId: number, sbId: number) =>
  api.post<UserWithRoles>(`/admin/users/${userId}/sanctioning-bodies`, { sanctioning_body_id: sbId }).then((r) => r.data);

export const removeSanctioningBodyFromUser = (userId: number, sbId: number) =>
  api.delete<UserWithRoles>(`/admin/users/${userId}/sanctioning-bodies/${sbId}`).then((r) => r.data);

// Admin - Roles
export const getRoles = () =>
  api.get<Role[]>('/admin/users/roles/all').then((r) => r.data);

// App Users
export const getAppUsers = () =>
  api.get<AppUser[]>('/app-users').then((r) => r.data);

export const getAppUser = (id: number) =>
  api.get<AppUser>(`/app-users/${id}`).then((r) => r.data);

export const updateAppUser = (id: number, data: Partial<AppUser>) =>
  api.put<AppUser>(`/app-users/${id}`, data).then((r) => r.data);

export const createAppUser = (data: Partial<AppUser>) =>
  api.post<AppUser>('/app-users', data).then((r) => r.data);

export const deleteAppUser = (id: number) =>
  api.delete(`/app-users/${id}`).then((r) => r.data);

export const getAppUserVehicles = (appUserId: number) =>
  api.get<AppUserVehicle[]>(`/app-users/${appUserId}/vehicles`).then((r) => r.data);

export const replaceAppUserVehicles = (appUserId: number, vehicles: Partial<AppUserVehicle>[]) =>
  api.put<AppUserVehicle[]>(`/app-users/${appUserId}/vehicles`, { vehicles }).then((r) => r.data);

export const getAppUserRegistrationProfile = (id: number) =>
  api.get<Record<string, any>>(`/app-users/${id}/registration-profile`).then((r) => r.data);

export default api;
